import { createFileRoute, Outlet, useMatchRoute } from "@tanstack/react-router";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { ExternalLink } from "lucide-react";
import { Logo } from "~/components/ui/logo";
import {
  normalizePostAuthIntentSearchValue,
  resolvePostAuthRedirectPath,
} from "~/features/auth/auth-routing";
import { redirectIfAuthenticated } from "~/lib/auth/guards/route-guards";
import { getI18n } from "~/lib/i18n/i18n";
import { createPageHead } from "~/shared/lib/page-head";
import { cn } from "~/shared/lib/utils";
import { getBrand } from "~/tenant";

export const Route = createFileRoute("/auth")({
  component: RouteComponent,
  beforeLoad: async ({ context, location }) => {
    const search = location.search as {
      intent?: unknown;
      redirect?: unknown;
      reason?: unknown;
    };
    const requestedRedirect =
      typeof search.redirect === "string" ? search.redirect : undefined;
    const requestedIntent = normalizePostAuthIntentSearchValue(search.intent);
    // reason=unauthorized means the redirect target just rejected this user's
    // valid session (authorization, not authentication). Bouncing back to it
    // would immediately 401 again — a hard-reload loop between the target and
    // this page. Send them to the dashboard, which only needs authentication.
    const rejectedWhileAuthenticated = search.reason === "unauthorized";
    redirectIfAuthenticated({
      user: context.user,
      redirectTo: rejectedWhileAuthenticated
        ? "/dashboard"
        : resolvePostAuthRedirectPath(requestedRedirect, "/dashboard", requestedIntent),
    });
  },
  head: () => createPageHead(getI18n()._(msg`Authentication`)),
});

function RouteComponent() {
  const { i18n } = useLingui();
  const matchRoute = useMatchRoute();
  const brand = getBrand();
  const isRoomy = Boolean(matchRoute({ to: "/auth/reset-password", fuzzy: true }));
  const marketingUrl = brand.marketingUrl;
  const marketingHost = marketingUrl?.replace(/^https?:\/\//, "");
  const canvas = resolveAuthCanvas({
    brandName: brand.name,
    i18n,
    isForgotPassword: Boolean(matchRoute({ to: "/auth/forgot-password" })),
    isLogin: Boolean(matchRoute({ to: "/auth/login" })),
    isResetPassword: Boolean(matchRoute({ to: "/auth/reset-password", fuzzy: true })),
    isSignup: Boolean(matchRoute({ to: "/auth/signup" })),
  });

  return (
    <div className="bg-muted relative min-h-screen overflow-hidden">
      <div
        aria-hidden="true"
        className="from-background via-background/35 to-background pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(158,23,3,0.07),transparent_28%),linear-gradient(to_bottom,transparent,rgba(38,29,24,0.05))]"
      />

      <main
        id="main-content"
        tabIndex={-1}
        className="relative mx-auto grid min-h-screen w-full max-w-6xl gap-10 px-6 py-8 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(384px,448px)] lg:items-center lg:px-10"
      >
        <section className="hidden min-w-0 lg:flex lg:flex-col lg:justify-between lg:gap-10">
          <div className="space-y-8">
            <div className="flex items-center gap-4">
              <Logo className="h-12 w-auto" priority />
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.18em]">
                  {i18n._(msg`Member access`)}
                </p>
                <p className="text-foreground text-lg font-semibold">{brand.name}</p>
              </div>
            </div>

            <div className="max-w-xl space-y-4">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.2em]">
                {canvas.kicker}
              </p>
              <h1 className="text-foreground max-w-[14ch] text-4xl font-semibold tracking-tight text-balance">
                {canvas.title}
              </h1>
              <p className="text-muted-foreground max-w-[62ch] text-base leading-7">
                {canvas.description}
              </p>
            </div>

            <dl className="grid max-w-2xl gap-4 sm:grid-cols-2">
              {canvas.highlights.map((highlight) => (
                <div
                  key={highlight.label}
                  className="border-border/70 bg-background/72 rounded-2xl border p-4"
                >
                  <dt className="text-foreground text-sm font-medium">
                    {highlight.label}
                  </dt>
                  <dd className="text-muted-foreground mt-2 text-sm leading-6">
                    {highlight.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="border-border/70 bg-background/72 max-w-xl rounded-2xl border p-5">
            <p className="text-foreground text-sm font-medium">{canvas.supportTitle}</p>
            <p className="text-muted-foreground mt-2 text-sm leading-6">
              {canvas.supportDescription}
            </p>
            {marketingUrl ? (
              <a
                href={marketingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground mt-4 inline-flex items-center gap-2 text-sm font-medium underline underline-offset-4"
              >
                {i18n._(msg`Visit ${marketingHost ?? marketingUrl}`)}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : null}
          </div>
        </section>

        <section className="flex min-w-0 flex-col gap-6 lg:items-end">
          <div className="flex items-center gap-3 lg:hidden">
            <Logo className="h-10 w-auto" priority />
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.18em]">
                {canvas.kicker}
              </p>
              <p className="text-foreground text-base font-semibold">{brand.name}</p>
            </div>
          </div>

          <div className="border-border/70 bg-background/95 w-full rounded-[1.75rem] border p-6 shadow-sm sm:p-8">
            {/*
             * A-5: default surface width is compact (384px). Reset-password with a
             * live strength meter opts into the roomy variant (448px). French
             * strings expand ~30%; the wider variant keeps those surfaces from
             * cramping while keeping single-field flows (login, forgot) tight.
             */}
            <div
              className={cn("w-full", isRoomy ? "auth-surface-roomy" : "auth-surface")}
            >
              <Outlet />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

type AuthCanvasArgs = {
  brandName: string;
  i18n: ReturnType<typeof useLingui>["i18n"];
  isForgotPassword: boolean;
  isLogin: boolean;
  isResetPassword: boolean;
  isSignup: boolean;
};

function resolveAuthCanvas({
  brandName,
  i18n,
  isForgotPassword,
  isLogin,
  isResetPassword,
  isSignup,
}: AuthCanvasArgs) {
  if (isSignup) {
    return {
      kicker: i18n._(msg`Member portal`),
      title: i18n._(msg`Join ${brandName}`),
      description: i18n._(
        msg`Create an account to register for events, manage your membership, and access team tools.`,
      ),
      highlights: [],
      supportTitle: i18n._(msg`Already have an account?`),
      supportDescription: i18n._(
        msg`If you've registered, paid for a membership, or joined a team before, sign in instead — your account is already there.`,
      ),
    };
  }

  if (isForgotPassword || isResetPassword) {
    return {
      kicker: i18n._(msg`Account recovery`),
      title: i18n._(msg`Get back into your account without guessing`),
      description: i18n._(
        msg`Recovery stays on the same path as regular sign-in, with explicit next steps and the same access options once your password is reset.`,
      ),
      highlights: [
        {
          label: i18n._(msg`Same account, same access`),
          description: i18n._(
            msg`Resetting a password adds email sign-in to your existing account. Social and passkey access can still stay in place.`,
          ),
        },
        {
          label: i18n._(msg`Clear next step`),
          description: i18n._(
            msg`Request a link, confirm the new password, then return directly to sign-in.`,
          ),
        },
      ],
      supportTitle: i18n._(msg`Need public information instead?`),
      supportDescription: i18n._(
        msg`If you were looking for schedules, league news, or general member information, the public ${brandName} site remains available outside the portal.`,
      ),
    };
  }

  if (isLogin) {
    return {
      kicker: i18n._(msg`Member portal`),
      title: i18n._(msg`Sign in to ${brandName}`),
      description: i18n._(
        msg`Use the email you use for ${brandName} memberships, event registration, or team access.`,
      ),
      highlights: [],
      supportTitle: i18n._(msg`Looking for the public site?`),
      supportDescription: i18n._(
        msg`News, policies, and general ${brandName} information are still on the public site.`,
      ),
    };
  }

  return {
    kicker: i18n._(msg`Account access`),
    title: i18n._(msg`Use the path that fits your account`),
    description: i18n._(
      msg`Sign in, recover access, or create an account without losing the quiet, step-by-step flow members need on the way in.`,
    ),
    highlights: [
      {
        label: i18n._(msg`Member-first`),
        description: i18n._(
          msg`The portal prioritizes clear, low-friction account access over promotional messaging.`,
        ),
      },
      {
        label: i18n._(msg`Built for league operations`),
        description: i18n._(
          msg`Registration, membership, and team workflows stay behind one consistent access model.`,
        ),
      },
    ],
    supportTitle: i18n._(msg`Public site`),
    supportDescription: i18n._(
      msg`General information for ${brandName} remains available outside the sign-in flow whenever members are not trying to access account data.`,
    ),
  };
}
